import React, { useState, useEffect } from 'react';
import { getSuperAdminEmails } from "../utils/superAdminEmails";
import { 
  Container, 
  Typography, 
  Paper, 
  Box, 
  TextField,
  Button,
  Grid,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  InputLabel,
  CircularProgress,
  Alert
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, setDoc } from 'firebase/firestore';
import { firestore } from '../firebase.config';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';

// Helper function to generate a new Firestore document ID on the client
const generateNewTenantId = () => {
    return doc(collection(firestore, 'Tenants')).id;
};

const TenantManagement = () => {
  const [formData, setFormData] = useState({ 
    tenantId: generateNewTenantId(), // Pre-generate ID for a new tenant
    tenantName: '',
    adminName: '',
    adminEmailId: '',
    adminRole: 'AppAdmin',
    status: 'active'
  });

  const [tenants, setTenants] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const checkAccessAndFetchData = async () => {
      if (!currentUser) {
        console.log("No current user found, redirecting to signin.");
        navigate('/signin', { replace: true });
        return;
      }
      
      const superAdminEmails = getSuperAdminEmails();
      const userEmail = currentUser.email.trim().toLowerCase();
      const isSuperAdmin = superAdminEmails.includes(userEmail);

      if (!isSuperAdmin) {
        console.log("Access Denied: User is not a Super Admin.");
        setAccessDenied(true);
        setIsLoading(false);
        setTimeout(() => navigate('/homepage', { replace: true }), 3000);
        return;
      }

      console.log("Access Granted: Super Admin confirmed.");
      await fetchTenants();
      setIsLoading(false);
    };

    checkAccessAndFetchData();
  }, [currentUser, navigate]);

  const fetchTenants = async () => {
    try {
      const tenantsRef = collection(firestore, "Tenants");
      const querySnapshot = await getDocs(tenantsRef);
      const tenantsData = querySnapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
      setTenants(tenantsData);
    } catch (error) {
      console.error("Error fetching tenants:", error);
      setErrors({
        fetch: "Failed to load tenants. Please refresh the page."
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    let tempErrors = {};
    if (!formData.tenantName.trim()) tempErrors.tenantName = 'Tenant Name is required';
    if (!formData.adminName.trim()) tempErrors.adminName = 'Admin Name is required';
    if (!formData.adminEmailId.trim()) {
      tempErrors.adminEmailId = 'Admin Email ID is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.adminEmailId)) {
      tempErrors.adminEmailId = 'Invalid email format';
    }
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setErrors({});

    try {
      if (selectedTenant) {
        // Update existing tenant
        await updateDoc(doc(firestore, "Tenants", selectedTenant.id), {
          tenantName: formData.tenantName,
          adminName: formData.adminName,
          adminEmailId: formData.adminEmailId,
          adminRole: formData.adminRole,
          status: formData.status,
          updatedBy: currentUser.email,
          updatedAt: new Date().toISOString()
        });
        setSubmitSuccess(true);
        handleCancelEdit(); // Reset form after update
      } else {
        // Create new tenant using the pre-generated ID from formData
        const newTenantRef = doc(firestore, "Tenants", formData.tenantId);
        const newTenantData = {
          tenantName: formData.tenantName,
          adminName: formData.adminName,
          adminEmailId: formData.adminEmailId,
          adminRole: formData.adminRole,
          status: formData.status,
          createdBy: currentUser.email,
          createdAt: new Date().toISOString()
        };
        await setDoc(newTenantRef, newTenantData);

        const generatedId = formData.tenantId;

        // Use generatedId when creating the UserProfileMaster entry
        await addDoc(collection(firestore, "UserProfileMaster"), {
          displayName: formData.adminName,
          email: formData.adminEmailId,
          tenantId: generatedId,
          tenantName: formData.tenantName,
          role: formData.adminRole,
          status: 'active',
          createdBy: currentUser.email,
          createdAt: new Date().toISOString()
        });
        
        const newTenantForState = { id: generatedId, ...newTenantData };
        setSelectedTenant(newTenantForState);
        setSubmitSuccess(true);
      }

      await fetchTenants();
      setTimeout(() => setSubmitSuccess(false), 3000);

    } catch (error) {
      console.error("Submit error:", error);
      setErrors({ submission: `Operation failed: ${error.message}` });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (tenant) => {
    setSelectedTenant(tenant);
    setFormData({
      tenantId: tenant.id || '',
      tenantName: tenant.tenantName,
      adminName: tenant.adminName,
      adminEmailId: tenant.adminEmailId,
      adminRole: tenant.adminRole || 'AppAdmin',
      status: tenant.status || 'active'
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setSelectedTenant(null);
    setFormData({
      tenantId: generateNewTenantId(), // Generate a new ID for the next potential tenant
      tenantName: '',
      adminName: '',
      adminEmailId: '',
      adminRole: 'AppAdmin',
      status: 'active'
    });
    setErrors({});
  };

  const handleDelete = async (tenantDocId) => {
    if (window.confirm('Are you sure you want to delete this tenant?')) {
      try {
        await deleteDoc(doc(firestore, "Tenants", tenantDocId));
        await fetchTenants();
        setSubmitSuccess(true);
        setTimeout(() => setSubmitSuccess(false), 3000);
      } catch (error) {
        console.error("Delete error:", error);
        setErrors({ submission: "Failed to delete tenant." });
      }
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh', flexDirection: 'column', gap: 2 }}>
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary">Loading Tenant Management...</Typography>
        </Box>
      </Container>
    );
  }

  if (accessDenied) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ p: 3, mt: 4 }}>
          <Alert severity="error">
            <Typography variant="h6">Access Denied</Typography>
            <Typography>You are not authorized to view this page.</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>Redirecting...</Typography>
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
          Tenant Management
        </Typography>
        
        {submitSuccess && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Operation successful!
          </Alert>
        )}

        {errors.submission && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errors.submission}
          </Alert>
        )}

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {selectedTenant ? 'Edit Tenant' : 'Add New Tenant'}
          </Typography>
          <form onSubmit={handleSubmit} noValidate>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Tenant ID"
                  name="tenantId"
                  value={formData.tenantId}
                  fullWidth
                  disabled
                  helperText={selectedTenant ? "Firestore document ID (primary tenant identifier)" : "Auto-generated Firestore document ID"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Tenant Name"
                  name="tenantName"
                  value={formData.tenantName}
                  onChange={handleChange}
                  fullWidth
                  required
                  error={!!errors.tenantName}
                  helperText={errors.tenantName || 'Display name for the tenant'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Admin Name"
                  name="adminName"
                  value={formData.adminName}
                  onChange={handleChange}
                  fullWidth
                  required
                  error={!!errors.adminName}
                  helperText={errors.adminName || 'Name of the tenant administrator'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Admin Email ID"
                  name="adminEmailId"
                  value={formData.adminEmailId}
                  onChange={handleChange}
                  fullWidth
                  required
                  type="email"
                  error={!!errors.adminEmailId}
                  helperText={errors.adminEmailId || 'Email address of the administrator'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Admin Role"
                  name="adminRole"
                  value="App Admin"
                  fullWidth
                  disabled
                  helperText="The role is automatically set to App Admin."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="status-label">Status</InputLabel>
                  <Select
                    labelId="status-label"
                    label="Status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
                    {isSubmitting ? 'Submitting...' : (selectedTenant ? 'Update Tenant' : 'Add Tenant')}
                  </Button>
                  {selectedTenant && (
                    <Button variant="outlined" onClick={handleCancelEdit} disabled={isSubmitting}>
                      Cancel Edit
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>

        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Existing Tenants ({tenants.length})
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Tenant ID</strong></TableCell>
                  <TableCell><strong>Tenant Name</strong></TableCell>
                  <TableCell><strong>Admin Email</strong></TableCell>
                  <TableCell><strong>Role</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tenants.map((tenant) => (
                  <TableRow key={tenant.id} hover sx={{ backgroundColor: selectedTenant?.id === tenant.id ? 'action.hover' : 'inherit' }}>
                    <TableCell>{tenant.id}</TableCell>
                    <TableCell>{tenant.tenantName}</TableCell>
                    <TableCell>{tenant.adminEmailId}</TableCell>
                    <TableCell>{tenant.adminRole || 'AppAdmin'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: tenant.status === 'active' ? 'success.main' : 'error.main', fontWeight: 'bold' }}>
                        {tenant.status?.toUpperCase() || 'ACTIVE'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <IconButton color="primary" onClick={() => handleEdit(tenant)}><Edit /></IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton color="error" onClick={() => handleDelete(tenant.id)}><Delete /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Container>
  );
};

export default TenantManagement;